import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthLayout from '@/layouts/AuthLayout';
import { Field, OtpInput, inputClass } from '@/components/forms/form';
import { Button } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { OTP_LENGTH } from '@/lib/config';

const RESEND_SECONDS = 60;

export default function Signup() {
  const navigate = useNavigate();
  const { sendCode, verifyCode, session, loading } = useAuth();

  const [step, setStep] = useState<'details' | 'code'>('details');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isAdult, setIsAdult] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  const canSubmit = name.trim().length >= 2 && emailValid && isAdult;

  useEffect(() => {
    if (!loading && session) navigate('/companions', { replace: true });
  }, [loading, session, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const submitDetails = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError('');
    try {
      // createUser: true — this is the one place an account may be created.
      // displayName rides along in user metadata and is picked up by the
      // handle_new_user() trigger when the profile row is created.
      await sendCode(email, { createUser: true, displayName: name });
      setStep('code');
      setCooldown(RESEND_SECONDS);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async () => {
    if (code.length < OTP_LENGTH || busy) return;
    setBusy(true);
    setError('');
    try {
      await verifyCode(email, code);
      navigate('/companions', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      image="auth-evening.jpg"
      imageAlt="A window seat at dusk with warm light spilling in"
      quote="You do not need a reason, and you do not need to be in crisis."
      attribution="How this works"
    >
      {step === 'details' ? (
        <>
          <h1 className="text-3xl font-semibold">Create your account</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">
            Takes about a minute. No card needed until you top up.
          </p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitDetails();
            }}
          >
            <Field id="name" label="What should we call you?" hint="A first name is plenty.">
              <input
                id="name"
                type="text"
                autoComplete="given-name"
                autoFocus
                placeholder="Rahul"
                className={inputClass}
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Field>

            <Field
              id="email"
              label="Email address"
              error={error}
              hint="We send a code here to sign you in."
            >
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                className={inputClass}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
              />
            </Field>

            {/* 18+ is a legal gate. Also enforced by a CHECK constraint. */}
            <label className="flex cursor-pointer items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={isAdult}
                onChange={(e) => setIsAdult(e.target.checked)}
                className="mt-0.5 size-4.5 shrink-0 accent-[var(--marigold)]"
              />
              <span className="text-[var(--ink-2)]">
                I am 18 or older, and I accept the{' '}
                <Link
                  to="/terms"
                  target="_blank"
                  className="text-[var(--marigold)] underline-offset-4 hover:underline"
                >
                  terms
                </Link>{' '}
                and{' '}
                <Link
                  to="/privacy"
                  target="_blank"
                  className="text-[var(--marigold)] underline-offset-4 hover:underline"
                >
                  privacy policy
                </Link>
                .
              </span>
            </label>

            <Button size="lg" full type="submit" disabled={!canSubmit || busy}>
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          </form>

          <p className="mt-7 text-sm text-[var(--ink-2)]">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
            >
              Log in
            </Link>
          </p>

          <div className="mt-8 border-t border-[var(--line)] pt-6">
            <p className="text-sm text-[var(--muted)]">
              Want to earn as a companion instead?{' '}
              <Link
                to="/for-companions"
                className="font-medium text-[var(--ink-2)] underline-offset-4 hover:text-[var(--ink)] hover:underline"
              >
                See how it works
              </Link>
            </p>
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => {
              setStep('details');
              setCode('');
              setError('');
            }}
            className="mb-6 text-sm text-[var(--ink-2)] hover:text-[var(--ink)]"
          >
            ← Change details
          </button>

          <h1 className="text-3xl font-semibold">Check your email</h1>
          <p className="mt-2.5 text-[var(--ink-2)]">
            We sent a 6-digit code to <span className="font-medium text-[var(--ink)]">{email}</span>
          </p>

          <form
            className="mt-8 flex flex-col gap-5"
            onSubmit={(e) => {
              e.preventDefault();
              void submitCode();
            }}
          >
            <OtpInput value={code} onChange={setCode} />

            {error && (
              <p className="text-sm" style={{ color: 'var(--danger)' }}>
                {error}
              </p>
            )}

            <Button size="lg" full type="submit" disabled={code.length < OTP_LENGTH || busy}>
              {busy ? 'Creating account…' : 'Create account'}
            </Button>

            <p className="text-center text-sm text-[var(--muted)]">
              {cooldown > 0 ? (
                <>
                  Resend in {Math.floor(cooldown / 60)}:
                  {(cooldown % 60).toString().padStart(2, '0')}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => void submitDetails()}
                  disabled={busy}
                  className="font-medium text-[var(--marigold)] underline-offset-4 hover:underline"
                >
                  Send another code
                </button>
              )}
            </p>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
